import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Spin, Divider, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { getMsalInstance, processMicrosoftRedirectResult } from "../../modules/msalService";
import { SESSION_TOKEN } from "../../constants/constants";

// ─── Styles ──────────────────────────────────────────────────────────────────
const page   = { minHeight: "100vh", background: "#0f172a", padding: "40px 20px", fontFamily: "'Segoe UI', sans-serif" };
const wrap   = { maxWidth: 860, margin: "0 auto" };
const card   = { background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "20px 24px", marginBottom: 16 };
const sec    = { color: "#e2e8f0", fontSize: 15, fontWeight: 600, borderBottom: "1px solid #334155", paddingBottom: 8, marginBottom: 16 };
const lbl    = { color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 };
const val    = { color: "#f1f5f9", fontSize: 13, marginBottom: 14, wordBreak: "break-all" };
const btnPri = { padding: "9px 22px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600 };
const btnSec = { padding: "9px 22px", background: "#334155", color: "#e2e8f0", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14 };

function Field({ label: l, value: v }) {
    return (
        <div>
            <div style={lbl}>{l}</div>
            <div style={val}>{v !== undefined && v !== null && v !== "" ? String(v) : <span style={{ color: "#475569" }}>—</span>}</div>
        </div>
    );
}

/** Parse the MSAL interactionType from the URL's state parameter. */
function getMsalInteractionType() {
    // MSAL puts the auth code in the URL hash: /auth-callback#code=...&state=...
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment || window.location.search.substring(1));
    const code = params.get("code");
    if (!code) return null; // no auth code in URL

    const stateRaw = params.get("state");
    if (!stateRaw) return "unknown";

    try {
        // MSAL state is URL-encoded then base64-encoded JSON
        const decoded = atob(decodeURIComponent(stateRaw));
        return JSON.parse(decoded)?.meta?.interactionType || "unknown";
    } catch (_) {
        return "unknown";
    }
}
// ─────────────────────────────────────────────────────────────────────────────

export default function MicrosoftAuthCallback() {
    const history = useHistory();
    const [status, setStatus] = useState("processing");
    const [data,   setData]   = useState(null);
    const [error,  setError]  = useState(null);

    useEffect(() => {
        async function process() {
            const interactionType = getMsalInteractionType();

            // ── CASE 1: Popup window ─────────────────────────────────────────
            // interactionType === "popup" means this page loaded inside MSAL's
            // popup window after Microsoft redirected it back.
            //
            // We CANNOT rely on window.opener to detect this — Microsoft's login
            // pages clear window.opener for security before redirecting back, so
            // window.opener is null even inside a popup.
            //
            // Instead we detect it from the MSAL state parameter in the URL.
            // Calling getMsalInstance() triggers handleRedirectPromise(), which
            // exchanges the auth code for tokens and broadcasts the result to the
            // main window via BroadcastChannel. MSAL then closes this popup.
            if (interactionType === "popup") {
                try { await getMsalInstance(); } catch (_) {}
                // Status stays "processing" — MSAL closes this window via window.close().
                return;
            }

            // ── CASE 2: Redirect flow (popup was blocked by browser) ─────────
            // interactionType === "redirect" means the browser blocked the popup
            // and MSAL fell back to a full-page redirect. The auth code is in the
            // URL hash of the MAIN window. Process it with handleMicrosoftRedirect().
            if (interactionType === "redirect") {
                try {
                    const result = await processMicrosoftRedirectResult();
                    if (result) {
                        const backendObj = result.backendResult?.Obj;
                        if (backendObj?.AuthToken) {
                            localStorage.setItem(SESSION_TOKEN, backendObj.AuthToken);
                            localStorage.setItem("userRole", backendObj.Role || "");
                        }
                        setData(result);
                        setStatus("success");
                    } else {
                        setStatus("no_response");
                    }
                } catch (err) {
                    setError(err);
                    setStatus("error");
                }
                return;
            }

            // ── CASE 3: Main window after successful popup ───────────────────
            // No auth code in URL. loginWithMicrosoft() resolved in the main
            // window, stored the result in localStorage, and navigated here.
            try {
                const stored = localStorage.getItem("ssoLoginResult");
                if (stored) {
                    setData(JSON.parse(stored));
                    setStatus("success");
                } else {
                    setStatus("no_response");
                }
            } catch (err) {
                setError(err);
                setStatus("error");
            }
        }
        process();
    }, []);

    // ── Popup-window spinner (case 1) ─────────────────────────────────────
    // interactionType is "popup" so this IS the popup window.
    // MSAL will close it shortly — just show a loading screen.
    if (getMsalInteractionType() === "popup" && status === "processing") {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#0f172a" }}>
                <div style={{ textAlign: "center" }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: "#60a5fa" }} spin />} />
                    <p style={{ color: "#64748b", marginTop: 16, fontSize: 13 }}>Completing sign-in…</p>
                </div>
            </div>
        );
    }

    // ── Processing ────────────────────────────────────────────────────────
    if (status === "processing") {
        return (
            <div style={{ ...page, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: "#60a5fa" }} spin />} />
                    <p style={{ color: "#94a3b8", marginTop: 20, fontSize: 15 }}>Processing Microsoft authentication…</p>
                </div>
            </div>
        );
    }

    // ── No data ───────────────────────────────────────────────────────────
    if (status === "no_response") {
        return (
            <div style={{ ...page, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ ...card, textAlign: "center", maxWidth: 460 }}>
                    <CloseCircleOutlined style={{ fontSize: 36, color: "#f87171", marginBottom: 12 }} />
                    <div style={{ color: "#f87171", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Authentication Data</div>
                    <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 20 }}>
                        Click <strong style={{ color: "#fff" }}>"Sign in with Microsoft"</strong> on the login page to begin.
                    </div>
                    <button onClick={() => history.push("/")} style={btnSec}>← Back to Login</button>
                </div>
            </div>
        );
    }

    // ── Error ─────────────────────────────────────────────────────────────
    if (status === "error") {
        return (
            <div style={page}>
                <div style={wrap}>
                    <PageHeader />
                    <div style={{ ...card, borderColor: "#7f1d1d", background: "#450a0a", marginBottom: 20 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <CloseCircleOutlined style={{ color: "#f87171", fontSize: 26 }} />
                            <div>
                                <div style={{ color: "#f87171", fontWeight: 600, fontSize: 15 }}>Authentication Failed</div>
                                <div style={{ color: "#fca5a5", fontSize: 13, marginTop: 2 }}>{error?.message || "Unknown error"}</div>
                            </div>
                        </div>
                    </div>
                    <div style={card}>
                        <div style={sec}>Error Details</div>
                        <Field label="Error Code"    value={error?.errorCode} />
                        <Field label="Error Message" value={error?.errorMessage || error?.message} />
                        <Field label="Sub Error"     value={error?.subError} />
                        <Field label="Correlation ID" value={error?.correlationId} />
                        {error?.errorCode && (
                            <a href={`https://aka.ms/msal.js.errors#${error.errorCode}`} target="_blank" rel="noreferrer"
                               style={{ color: "#60a5fa", fontSize: 13 }}>
                                MSAL error docs: #{error.errorCode} ↗
                            </a>
                        )}
                    </div>
                    {error?.stack && (
                        <div style={card}>
                            <div style={sec}>Stack Trace</div>
                            <pre style={{ color: "#94a3b8", fontSize: 11, overflowX: "auto", background: "#0f172a", padding: 12, borderRadius: 6, margin: 0 }}>
                                {error.stack}
                            </pre>
                        </div>
                    )}
                    <Actions history={history} />
                </div>
            </div>
        );
    }

    // ── Success ───────────────────────────────────────────────────────────
    const { msalAccount, idTokenClaims, idToken, backendResult } = data;
    const backend     = backendResult?.Obj || {};
    const isBackendOk = backendResult?.IsSuccess;
    const userFound   = backend?.UserFoundInDatabase;

    return (
        <div style={page}>
            <div style={wrap}>
                <PageHeader />

                <div style={{ ...card, borderColor: "#14532d", background: "#052e16", display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <CheckCircleOutlined style={{ color: "#4ade80", fontSize: 26 }} />
                    <div>
                        <div style={{ color: "#4ade80", fontWeight: 600, fontSize: 15 }}>Microsoft Authentication Successful</div>
                        <div style={{ color: "#86efac", fontSize: 13, marginTop: 2 }}>Token received and validated</div>
                    </div>
                    {userFound && (
                        <Tag color="blue" style={{ marginLeft: "auto", fontSize: 13 }}>
                            {backend.UserType} — {backend.Role}
                        </Tag>
                    )}
                </div>

                <div style={card}>
                    <div style={sec}>Microsoft Account (msalInstance.getActiveAccount())</div>
                    <Field label="username"       value={msalAccount?.username} />
                    <Field label="name"           value={msalAccount?.name} />
                    <Field label="tenantId"       value={msalAccount?.tenantId} />
                    <Field label="homeAccountId"  value={msalAccount?.homeAccountId} />
                    <Field label="localAccountId" value={msalAccount?.localAccountId} />
                    <Field label="environment"    value={msalAccount?.environment} />
                </div>

                <div style={card}>
                    <div style={sec}>ID Token Claims</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...lbl, textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #334155" }}>Claim</th>
                                    <th style={{ ...lbl, textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #334155" }}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(idTokenClaims || {}).map(([k, v]) => (
                                    <tr key={k} style={{ borderBottom: "1px solid #1e293b" }}>
                                        <td style={{ padding: "6px 10px", color: "#7dd3fc", fontFamily: "monospace" }}>{k}</td>
                                        <td style={{ padding: "6px 10px", color: "#e2e8f0", wordBreak: "break-all" }}>
                                            {typeof v === "object" ? JSON.stringify(v) : String(v)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {idToken && (
                    <div style={card}>
                        <div style={sec}>Raw ID Token (JWT)</div>
                        <div style={{ fontFamily: "monospace", fontSize: 11, color: "#4ade80", wordBreak: "break-all", background: "#0f172a", padding: 12, borderRadius: 6 }}>
                            {idToken}
                        </div>
                    </div>
                )}

                {backendResult && (
                    <div style={card}>
                        <div style={sec}>
                            Backend Validation (Levridge API)&nbsp;
                            {isBackendOk ? <Tag color="green">Success</Tag> : <Tag color="red">Failed — {backendResult?.Message}</Tag>}
                        </div>
                        <Field label="Email"               value={backend.Email} />
                        <Field label="Display Name"        value={backend.DisplayName} />
                        <Field label="Microsoft Object ID" value={backend.MicrosoftObjectId} />
                        <Field label="Microsoft Tenant ID" value={backend.MicrosoftTenantId} />
                        <Field label="User Found in DB"    value={userFound ? `Yes — ${backend.UserType}` : "No"} />
                        {userFound && (
                            <>
                                <Field label="App User ID" value={backend.AppUserId} />
                                <Field label="Role"        value={backend.Role} />
                                <Field label="Role ID"     value={backend.RoleId} />
                                <Field label="JWT (first 60 chars)" value={backend.AuthToken ? backend.AuthToken.substring(0, 60) + "…" : null} />
                            </>
                        )}
                        {backend.MicrosoftClaims && Object.keys(backend.MicrosoftClaims).length > 0 && (
                            <>
                                <Divider style={{ borderColor: "#334155", margin: "14px 0 10px" }} />
                                <div style={{ ...lbl, marginBottom: 8 }}>All Claims (server-validated)</div>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                        <tbody>
                                            {Object.entries(backend.MicrosoftClaims).map(([k, v]) => (
                                                <tr key={k} style={{ borderBottom: "1px solid #1e293b" }}>
                                                    <td style={{ padding: "5px 10px", color: "#7dd3fc", fontFamily: "monospace", whiteSpace: "nowrap" }}>{k}</td>
                                                    <td style={{ padding: "5px 10px", color: "#e2e8f0", wordBreak: "break-all" }}>{v}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <Actions history={history} userFound={userFound} userType={backend.UserType} authToken={backend.AuthToken} />
            </div>
        </div>
    );
}

function PageHeader() {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            <div>
                <h1 style={{ color: "#f8fafc", margin: 0, fontSize: 22, fontWeight: 700 }}>Microsoft SSO — Authentication Response</h1>
                <p style={{ color: "#64748b", margin: 0, fontSize: 13 }}>All data received from Microsoft Identity Platform</p>
            </div>
        </div>
    );
}

function Actions({ history, userFound, userType, authToken }) {
    return (
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
            <button onClick={() => history.push("/")} style={btnSec}>← Back to Login</button>
            {userFound && authToken && (
                <button
                    onClick={() => {
                        const paths = { Banker: "/banker-dashboard", LoanOfficer: "/loanOfficerDashboard", BankAdmin: "/bankadmindashboard" };
                        history.push(paths[userType] || "/");
                    }}
                    style={btnPri}
                >
                    Go to Dashboard →
                </button>
            )}
        </div>
    );
}
